import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { Pollster, PollsterStartupBehavior } from 'lib/pollster';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { ActivityFeedResponse, ActivityFeedResponseOptions, ActivityFeedNewReservation } from 'data/models/ActivityFeedResponse';
import { BerthReservation } from 'data/models/BerthReservation';
import { DomainSummary } from 'data/models/DomainSummary';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { PCUser } from 'data/models/PCUser';
import { PortCallChange } from 'data/models/PortCallChange';
import { RecurringBatch } from 'data/models/RecurringBatch';
import { RecurringBatchChange } from 'data/models/RecurringBatchChange';
import { ScheduleDate } from 'data/models/ScheduleDate';
import { TopicCardChange } from 'data/models/TopicCardChange';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';

const POLLING_INTERVAL = 300000;

export const enum ActivityFeedTab {
    New,
    Changes,
    Conflicts,
    Open
}

export const enum LineTimeType {
    FirstLine,
    LastLine
}

export interface ActivityFeedParams {
    id : number;
    activityFeedTab : ActivityFeedTab;
}

export function ActivityFeed(
    loader : CachedModelLoader<ActivityFeedResponse, ActivityFeedResponseOptions>,
    domain : DomainSummary,
    tzutils : TZUtils,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    { id, activityFeedTab: _tab } : ActivityFeedParams
) {
    const { model } = loader({ OrganizationId: id }),
        tab = S.data(_tab),
        eventClock = tzutils.clock(1000), // clock for refreshing event time ago text
        missingActualsClock = tzutils.clock(30000), // clock for recalculating which actuals are missing on the Open tab
        userModel = (user : PCUser) => UserModel(user, personProfilePopupModel),
        newReservations = model.NewReservations.map(res => NewReservationModel(res, userModel)),
        newBatches = model.NewBatches.map(b => NewBatchModel(b, userModel)),
        changedReservations = model.ChangedReservations.map(c => ChangedReservationModel(c, userModel)),
        changedBatches = model.ChangedBatches.map(c => ChangedBatchModel(c, userModel)),
        changedTopicCards = model.ChangedTopicCards.map(c => ChangedTopicCardModel(c, userModel)),
        conflictedReservations = model.ConflictedReservations.map(ConflictedReservationModel),
        conflictedBatches = model.ConflictedBatches.map(ConflictedBatchModel),
        open = model.OpenReservations.map(res => OpenReservationModel(res, missingActualsClock)),
        missingActuals = open.reduce((list, next) => {
            if (next.MissingFirstLineActual()) list = list.concat(next.MissingFirstLineActual()!);
            if (next.MissingLastLineActual()) list = list.concat(next.MissingLastLineActual()!);
            return list;
        }, () => [] as MissingActualModel[]);

    new Pollster(() => loader({ OrganizationId: id }).task, POLLING_INTERVAL).start(PollsterStartupBehavior.Lazy);

    return {
        Model: model,
        New: (newReservations as ArraySignal<NewReservationModel | NewBatchModel>)
            .concat(newBatches)
            .sort(itemSort),
        Changes: (changedReservations as ArraySignal<ChangedReservationModel | ChangedBatchModel | ChangedTopicCardModel>)
            .concat(changedBatches)
            .concat(changedTopicCards)
            .sort(itemSort),
        Conflicts: (conflictedReservations as ArraySignal<ConflictedReservationModel | ConflictedBatchModel>)
            .concat(conflictedBatches)
            .sort(itemSort),
        Open: lift(missingActuals).sort(itemSort),
        Domain: domain,
        EventNow: () => eventClock.now(),
        Tab: tab
    };
}
export type ActivityFeed = ReturnType<typeof ActivityFeed>;

function itemSort(item1 : { SortOrder : () => number }, item2 : { SortOrder : () => number }) {
    return item1.SortOrder() - item2.SortOrder();
}

function NewReservationModel(
    item : ActivityFeedNewReservation,
    userModel : (user : PCUser) => UserModel
) {
    return {
        Key: () => 'reservation-' + item.Id(),
        Type: 'reservation' as 'reservation',
        Model: item,
        Requestor: S(() => item.Requestor() && userModel(item.Requestor()!)),
        ShowActions: S.data(false),
        // Chronologically by effective first line
        SortOrder: () => item.Reservation() && item.Reservation()!.Itinerary() ? +item.Reservation()!.Itinerary()!.Arrival.Effective() : 0
    };
}
export type NewReservationModel = ReturnType<typeof NewReservationModel>;

function NewBatchModel(
    item : RecurringBatch,
    userModel : (user : PCUser) => UserModel
) {
    const firstPortCall = S(() => item.FirstPortCall()),
        firstPoint = S(() => item.FirstReservationPoint());

    return {
        Key: () => 'batch-' + item.Id(),
        Type: 'batch' as 'batch',
        Model: item,
        Requestor: S(() => firstPortCall() && firstPortCall()!.Requestor() && userModel(firstPortCall()!.Requestor()!)),
        FirstPortCall: firstPortCall,
        FirstReservationPoint: firstPoint,
        ShowActions: S.data(false),
        // Chronologically by effective first line of first reservation
        SortOrder: () => firstPoint() ? +firstPoint()!.Arrival.Effective() : 0
    };
}
export type NewBatchModel = ReturnType<typeof NewBatchModel>;

function ChangedReservationModel(
    item : PortCallChange,
    userModel : (user : PCUser) => UserModel
) {
    return {
        Key: () => 'reservation-' + item.Id(),
        Type: 'reservation' as 'reservation',
        Model: item,
        Changer: S(() => item.Event() && item.Event()!.User() && userModel(item.Event()!.User()!)),
        ShowActions: S.data(false),
        // Reverse chronologically by event date
        SortOrder: () => item.Event() ? -item.Event()!.Date() : -Infinity
    };
}
export type ChangedReservationModel = ReturnType<typeof ChangedReservationModel>;

function ChangedBatchModel(
    item : RecurringBatchChange,
    userModel : (user : PCUser) => UserModel
) {
    const firstReservationChange = S(() => item.ReservationChanges().length > 0 ? item.ReservationChanges()[0] : null),
        firstPoint = S(() => item.Batch() && item.Batch()!.FirstReservationPoint());

    return {
        Key: () => 'batch-' + item.Id(),
        Type: 'batch' as 'batch',
        Model: item,
        ChangerId: () => firstReservationChange() && firstReservationChange()!.Event()! && firstReservationChange()!.Event()!.UserId(),
        Changer: S(() => firstReservationChange() && firstReservationChange()!.Event()! && firstReservationChange()!.Event()!.User() && userModel(firstReservationChange()!.Event()!.User()!)),
        NewStatus: () => firstReservationChange() && firstReservationChange()!.NewStatus(),
        FirstReservationPoint: firstPoint,
        ShowActions: S.data(false),
        // Reverse chronologically by event date
        SortOrder: () => item.Event() ? -item.Event()!.Date() : -Infinity
    };
}
export type ChangedBatchModel = ReturnType<typeof ChangedBatchModel>;

function ChangedTopicCardModel(
    item : TopicCardChange,
    userModel : (user : PCUser) => UserModel
) {
    return {
        Key: () => 'topiccard-' + item.Id(),
        Type: 'topiccard' as 'topiccard',
        Model: item,
        // TH 01/27/2017 -- Currently only expect changes w/ a Changer of null to show up here
        // b/c we only list status resets, not other kinds of topic card changes.
        // But future-proofing in case we ever list other kinds of changes.
        Changer: S(() => item.Event() && item.Event()!.User() && userModel(item.Event()!.User()!)),
        ShowActions: S.data(false),
        // Reverse chronologically by event date
        SortOrder: () => item.Event() ? -item.Event()!.Date() : -Infinity
    };
}
export type ChangedTopicCardModel = ReturnType<typeof ChangedTopicCardModel>;

function ConflictedReservationModel(item : BerthReservation) {
    return {
        Key: () => 'reservation-' + item.Id(),
        Type: 'reservation' as 'reservation',
        Model: item,
        ShowActions: S.data(false),
        // Chronologically by effective first line
        SortOrder: () => item.Itinerary() ? +item.Itinerary()!.Arrival.Effective() : 0
    };
}
export type ConflictedReservationModel = ReturnType<typeof ConflictedReservationModel>;

function ConflictedBatchModel(item : RecurringBatch) {
    const firstPoint = S(() => item.FirstReservationPoint());

    return {
        Key: () => 'batch-' + item.Id(),
        Type: 'batch' as 'batch',
        Model: item,
        FirstReservationPoint: firstPoint,
        ShowActions: S.data(false),
        // Chronologically by effect first line of first reservation
        SortOrder: () => firstPoint() ? +firstPoint()!.Arrival.Effective() : 0
    };
}
export type ConflictedBatchModel = ReturnType<typeof ConflictedBatchModel>;

function OpenReservationModel(
    item : BerthReservation,
    missingActualsClock : { now : () => TZDate }
) {
    const missingFirstLineActual = S(() => missing(pt => pt.Arrival) ? MissingActualModel(item, LineTimeType.FirstLine, pt => pt.Arrival) : null),
        missingLastLineActual = S(() => missing(pt => pt.Departure) ? MissingActualModel(item, LineTimeType.LastLine, pt => pt.Departure) : null);

    return {
        MissingFirstLineActual: missingFirstLineActual,
        MissingLastLineActual: missingLastLineActual
    };

    function missing(lineTime : (pt : ItineraryPoint) => ScheduleDate) {
        return !!item.Itinerary() && isMissingActual(lineTime(item.Itinerary()!), missingActualsClock);
    }
}
export type OpenReservationModel = ReturnType<typeof OpenReservationModel>;

function MissingActualModel(item : BerthReservation, lineType : LineTimeType, lineTime : (pt : ItineraryPoint) => ScheduleDate) {
    return {
        Key: () => item.Id() + ',' + lineType,
        Model: item,
        LineType: lineType,
        ShowActions: S.data(false),
        // Chronologically by whichever line time is missing
        SortOrder: () => item.Itinerary() ? +lineTime(item.Itinerary()!).Estimated() : 0
    };
}
export type MissingActualModel = ReturnType<typeof MissingActualModel>;

function isMissingActual(
    lineTime : ScheduleDate,
    missingActualsClock : { now : () => TZDate }
) {
    if (lineTime.Actual() !== null) return false; // actual time has been specified
    const now = S.sample(missingActualsClock.now);
    if (lineTime.Estimated() >= now) {
        // estimated hasn't occurred yet
        // TH 11/21/2016: Since missingActualsClock.now() can only move in one direction,
        // we only need to subscribe to the clock's now value if the estimated time is in the future.
        // Once the estimated time is in the past, it will stay in the past.
        missingActualsClock.now();
        return false;
    }
    // there's no actual and the estimate has already occurred
    return true;
}

function UserModel(
    user : PCUser,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const showProfile = S.data(false),
        profilePopup = S(() => showProfile() ? personProfilePopupModel({ id : user.Id(), onHidePopup : () => showProfile(false)}) : null);

    return {
        Model: user,
        ToggleProfile: () => showProfile(!showProfile()),
        ProfilePopup: profilePopup
    };
}
export type UserModel = ReturnType<typeof UserModel>;


// WEBPACK FOOTER //
// ./apps/portcall/berthmgrdashboard/activityfeed.ts